import React from "react";
import { Card } from "reactstrap";

/**
 * Building blocks for the side-by-side comparison. Every row, including the
 * sticky column headers, shares one grid (label | main | merge) so a value on
 * the left always sits level with its counterpart on the right.
 */

export interface CompareRowModel {
  key: string;
  label: string;
  main: React.ReactNode;
  merge: React.ReactNode;
  /** Both sides hold the same value; hidden when showing differences only. */
  same: boolean;
  /** Neither side has a value; always hidden. */
  empty: boolean;
}

export interface CompareSectionModel {
  key: string;
  title: string;
  icon: string;
  /** Tints the section's icon tile; danger marks risk on record. */
  tone?: "primary" | "danger";
  /** Short outcome shown in the section header, e.g. "2 move to main". */
  badge?: React.ReactNode;
  /** Rendered above the section's rows on each side, e.g. a Kept tag. */
  mainTag?: React.ReactNode;
  mergeTag?: React.ReactNode;
  rows: CompareRowModel[];
}

export const Empty = () => <span className="text-muted">—</span>;

/** Annotates a value on the merge side with what the merge does to it. */
export const Outcome = ({
  tone,
  icon,
  children,
}: {
  tone: "success" | "secondary" | "warning" | "info" | "danger";
  icon: string;
  children: React.ReactNode;
}) => (
  <span className={`merge-outcome text-ink-${tone === "secondary" ? "muted" : tone}`}>
    <i className={icon} aria-hidden="true"></i>
    {children}
  </span>
);

export const CompareRow = ({ row }: { row: CompareRowModel }) => (
  <div className={`compare-grid compare-row ${row.same ? "" : "compare-row--diff"}`}>
    <div className="compare-row__label">
      {row.label}
      {row.same ? null : <span className="visually-hidden"> (differs)</span>}
    </div>
    <div className="compare-row__cell compare-row__cell--main">
      <span className="compare-row__side-label">Main</span>
      {row.main}
    </div>
    <div className="compare-row__cell compare-row__cell--merge">
      <span className="compare-row__side-label">To merge</span>
      {row.merge}
    </div>
  </div>
);

export const CompareSection = ({
  section,
  rows,
}: {
  section: CompareSectionModel;
  rows: CompareRowModel[];
}) => (
  <Card className="compare-section">
    <div className="compare-section__header">
      <div className="d-flex align-items-center gap-2 flex-grow-1 merge-shrink">
        <span
          className={`compare-section__icon compare-section__icon--${
            section.tone || "primary"
          }`}
          aria-hidden="true"
        >
          <i className={section.icon}></i>
        </span>
        <h3 className="compare-section__title">{section.title}</h3>
      </div>
      {section.badge}
    </div>
    {section.mainTag || section.mergeTag ? (
      <div className="compare-grid compare-row compare-row--tags">
        <div className="compare-row__label"></div>
        <div className="compare-row__cell compare-row__cell--main">
          {section.mainTag}
        </div>
        <div className="compare-row__cell compare-row__cell--merge">
          {section.mergeTag}
        </div>
      </div>
    ) : null}
    {rows.map((row) => (
      <CompareRow key={row.key} row={row} />
    ))}
  </Card>
);
